import type { CameraController } from '../controller/CameraController';
import type { GameState, MapDefinition, TilePosition } from '../model/gamestate/GameState';
import type { TileMapRenderer } from '../renderer/tilemap/TileMapRenderer';
import { t } from '../i18n';
import { windowId, windowRegistry } from './windowing';

const REDRAW_INTERVAL_MS = 1000;
const HEADER_HEIGHT = 32;
const MINIMAP_MARGIN = 16;
const HUD_BOTTOM_OFFSET = 120;
const TILE_PX_MIN = 1;
const TILE_PX_MAX = 3;
const TARGET_MAX_AXIS_PX = 280;
const DISPLAY_MIN_WIDTH = 220;
const DISPLAY_MIN_HEIGHT = 150;
const ENABLE_VIEWPORT_OVERLAY = false;

const COLORS = {
  floor: '#2a2a3a',
  path: '#16a34a',
  wall: '#000000',
  portal: '#8b5cf6',
  townBorder: '#16a34a',
  defaultBorder: '#2a3142',
  viewportStroke: 'rgba(255, 255, 255, 0.85)',
  viewportFill: 'rgba(255, 255, 255, 0.10)',
  player: '#3b82f6',
  remoteFallback: '#f59e0b',
  mob: '#ef4444',
};

export interface MinimapHandle {
  show(): void;
  hide(): void;
  isVisible(): boolean;
  destroy(): void;
}

export function createMinimap(
  gameState: GameState,
  renderer: TileMapRenderer,
  cameraController: CameraController,
  username?: string
): MinimapHandle {
  void username;
  const defaultPosition = {
    x: Math.max(0, window.innerWidth - DISPLAY_MIN_WIDTH - MINIMAP_MARGIN),
    y: Math.max(0, window.innerHeight - HEADER_HEIGHT - HUD_BOTTOM_OFFSET),
  };

  let currentMapId = '';
  let currentMap: MapDefinition | null = null;
  let pixelsPerTile = 2;
  let mapCanvasWidth = 1;
  let mapCanvasHeight = 1;
  let displayCanvasWidth = DISPLAY_MIN_WIDTH;
  let displayCanvasHeight = DISPLAY_MIN_HEIGHT;
  let tileTextureCanvas: HTMLCanvasElement | null = null;

  const container = document.createElement('div');
  container.className = 'minimap-container window-container';
  Object.assign(container.style, {
    left: `${defaultPosition.x}px`,
    top: `${defaultPosition.y}px`,
    width: `${DISPLAY_MIN_WIDTH}px`,
    height: `${HEADER_HEIGHT + 150}px`,
    zIndex: '5001',
  });

  const header = document.createElement('div');
  header.className = 'window-header';

  const headerTitle = document.createElement('div');
  headerTitle.className = 'window-header-title';
  headerTitle.textContent = t('minimap.title');

  const closeButton = document.createElement('button');
  closeButton.type = 'button';
  closeButton.textContent = '×';
  closeButton.title = 'Hide minimap';
  Object.assign(closeButton.style, {
    marginLeft: 'auto',
    backgroundColor: 'transparent',
    border: 'none',
    color: '#9ca3af',
    fontSize: '1.2rem',
    cursor: 'pointer',
    lineHeight: '1',
    zIndex: '20',
  });

  const canvas = document.createElement('canvas');
  Object.assign(canvas.style, {
    display: 'block',
    width: '100%',
    height: 'auto',
    background: '#111827',
    borderTop: `1px solid ${COLORS.defaultBorder}`,
    cursor: 'crosshair',
    imageRendering: 'pixelated',
  });

  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Failed to create minimap canvas context');
  }
  ctx.imageSmoothingEnabled = false;

  header.appendChild(headerTitle);
  header.appendChild(closeButton);
  container.appendChild(header);
  container.appendChild(canvas);
  document.body.appendChild(container);
  const minimapWindow = windowRegistry.register({
    id: windowId('minimap', 'main'),
    container,
    header,
    closeButton,
    consumeEscape: false,
    minWidth: DISPLAY_MIN_WIDTH,
    minHeight: DISPLAY_MIN_HEIGHT,
    defaultPosition,
    defaultSize: { width: DISPLAY_MIN_WIDTH, height: HEADER_HEIGHT + 150 },
    stateChangeDebounceMs: 500,
  });
  minimapWindow.setActiveTab('map');

  function formatHeaderTitle(map: MapDefinition): string {
    return map.name || t('minimap.title');
  }

  function syncTownStyling(map: MapDefinition): void {
    const borderColor = map.mapKind === 'town' ? COLORS.townBorder : COLORS.defaultBorder;
    canvas.style.borderTop = `1px solid ${borderColor}`;
    container.style.borderColor = borderColor;
  }

  function syncMapTextureIfNeeded(): void {
    const map = gameState.getMap();
    const mapId = gameState.getCurrentMapId();
    if (mapId === currentMapId && map === currentMap && tileTextureCanvas) {
      return;
    }

    currentMapId = mapId;
    currentMap = map;

    headerTitle.textContent = formatHeaderTitle(map);
    syncTownStyling(map);

    const maxTiles = Math.max(1, Math.max(map.width, map.height));
    const suggested = Math.floor(TARGET_MAX_AXIS_PX / maxTiles);
    pixelsPerTile = Math.max(TILE_PX_MIN, Math.min(TILE_PX_MAX, suggested));

    const width = Math.max(1, map.width * pixelsPerTile);
    const height = Math.max(1, map.height * pixelsPerTile);
    mapCanvasWidth = width;
    mapCanvasHeight = height;
    canvas.width = width;
    canvas.height = height;

    const scale = Math.max(
      DISPLAY_MIN_WIDTH / mapCanvasWidth,
      DISPLAY_MIN_HEIGHT / mapCanvasHeight,
      1
    );
    displayCanvasWidth = Math.round(mapCanvasWidth * scale);
    displayCanvasHeight = Math.round(mapCanvasHeight * scale);
    canvas.style.width = `${displayCanvasWidth}px`;
    canvas.style.height = `${displayCanvasHeight}px`;
    applyExpandedLayout();

    const offscreen = document.createElement('canvas');
    offscreen.width = width;
    offscreen.height = height;
    const offscreenCtx = offscreen.getContext('2d');
    if (!offscreenCtx) {
      throw new Error('Failed to create minimap offscreen context');
    }
    offscreenCtx.imageSmoothingEnabled = false;

    for (let y = 0; y < map.height; y += 1) {
      const row = map.tiles[y];
      if (!row) continue;
      for (let x = 0; x < map.width; x += 1) {
        const tile = row[x];
        if (!tile) continue;
        const tilePxX = x * pixelsPerTile;
        const tilePxY = y * pixelsPerTile;
        if (tile.terrain === 'wall') {
          offscreenCtx.fillStyle = COLORS.wall;
          offscreenCtx.fillRect(tilePxX, tilePxY, pixelsPerTile, pixelsPerTile);
          continue;
        }

        // Portals render as floor + 1px center marker for consistency with tiny marker style.
        offscreenCtx.fillStyle = tile.terrain === 'path' ? COLORS.path : COLORS.floor;
        offscreenCtx.fillRect(tilePxX, tilePxY, pixelsPerTile, pixelsPerTile);
        if (tile.terrain === 'portal') {
          offscreenCtx.fillStyle = COLORS.portal;
          offscreenCtx.fillRect(
            Math.floor(tilePxX + pixelsPerTile / 2),
            Math.floor(tilePxY + pixelsPerTile / 2),
            1,
            1
          );
        }
      }
    }

    tileTextureCanvas = offscreen;
  }

  function applyExpandedLayout(): void {
    if (container.hidden) {
      return;
    }
    minimapWindow.setState({
      position: minimapWindow.getState().position,
      size: {
        width: displayCanvasWidth,
        height: HEADER_HEIGHT + displayCanvasHeight,
      },
    });
  }

  function drawSinglePixel(tile: TilePosition, color: string): void {
    if (!ctx) return;
    const x = Math.floor(tile.x * pixelsPerTile + pixelsPerTile / 2);
    const y = Math.floor(tile.y * pixelsPerTile + pixelsPerTile / 2);
    ctx.fillStyle = color;
    ctx.fillRect(x, y, 1, 1);
  }

  function drawViewportOverlay(): void {
    const viewSize = renderer.getViewSize();
    if (!viewSize.width || !viewSize.height) return;
    const samplePoints = [
      { x: 0, y: 0 },
      { x: viewSize.width, y: 0 },
      { x: 0, y: viewSize.height },
      { x: viewSize.width, y: viewSize.height },
      { x: viewSize.width / 2, y: viewSize.height / 2 },
      { x: viewSize.width / 2, y: 0 },
      { x: viewSize.width / 2, y: viewSize.height },
      { x: 0, y: viewSize.height / 2 },
      { x: viewSize.width, y: viewSize.height / 2 },
    ];

    const visibleTiles: TilePosition[] = [];
    for (const point of samplePoints) {
      const map = renderer.getCurrentMap();
      if (!map) continue;
      const tile = renderer.screenToTile(point.x, point.y);
      if (tile) {
        visibleTiles.push(tile);
      }
    }

    const focus = renderer.getCameraFocus();
    if (focus) {
      visibleTiles.push(focus);
    }
    if (visibleTiles.length === 0) {
      return;
    }

    const minX = Math.min(...visibleTiles.map((tile) => tile.x));
    const minY = Math.min(...visibleTiles.map((tile) => tile.y));
    const maxX = Math.max(...visibleTiles.map((tile) => tile.x));
    const maxY = Math.max(...visibleTiles.map((tile) => tile.y));

    const x = minX * pixelsPerTile;
    const y = minY * pixelsPerTile;
    const width = Math.max(1, (maxX - minX + 1) * pixelsPerTile);
    const height = Math.max(1, (maxY - minY + 1) * pixelsPerTile);

    if (!ctx) return;
    ctx.fillStyle = COLORS.viewportFill;
    ctx.fillRect(x, y, width, height);
    ctx.strokeStyle = COLORS.viewportStroke;
    ctx.lineWidth = 1;
    ctx.strokeRect(x + 0.5, y + 0.5, Math.max(0, width - 1), Math.max(0, height - 1));
  }

  function redraw(): void {
    if (container.hidden) {
      return;
    }

    syncMapTextureIfNeeded();
    if (!tileTextureCanvas || !currentMap) {
      return;
    }

    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(tileTextureCanvas, 0, 0);

    if (ENABLE_VIEWPORT_OVERLAY) {
      drawViewportOverlay();
    }

    const player = gameState.getCharacterPosition();
    drawSinglePixel(player, COLORS.player);

    for (const remote of gameState.getRemotePlayers()) {
      drawSinglePixel(remote.position, COLORS.remoteFallback);
    }

    for (const mob of gameState.getRuntimeMobs()) {
      drawSinglePixel(mob.position, COLORS.mob);
    }
  }

  function handleCanvasClick(event: MouseEvent): void {
    if (!currentMap) {
      return;
    }

    const rect = canvas.getBoundingClientRect();
    const clickX = (event.clientX - rect.left) * (canvas.width / rect.width);
    const clickY = (event.clientY - rect.top) * (canvas.height / rect.height);

    const tileX = Math.floor(clickX / pixelsPerTile);
    const tileY = Math.floor(clickY / pixelsPerTile);

    if (tileX < 0 || tileY < 0 || tileX >= currentMap.width || tileY >= currentMap.height) {
      return;
    }

    cameraController.panTo(tileX, tileY);
    redraw();
  }

  closeButton.addEventListener('mousedown', (event) => {
    event.preventDefault();
    event.stopPropagation();
  });
  closeButton.addEventListener('click', (event) => {
    event.preventDefault();
    event.stopPropagation();
    minimapWindow.setWindowState('closed');
  });
  canvas.addEventListener('click', handleCanvasClick);

  syncMapTextureIfNeeded();
  const restoredWindowState = minimapWindow.getWindowState();
  if (restoredWindowState === 'closed') {
    container.hidden = true;
    container.style.display = 'none';
  } else {
    minimapWindow.setWindowState('open');
    minimapWindow.setState({
      position: minimapWindow.getState().position,
      size: { width: displayCanvasWidth, height: HEADER_HEIGHT + displayCanvasHeight },
    });
  }
  redraw();

  const intervalId = window.setInterval(() => {
    redraw();
  }, REDRAW_INTERVAL_MS);

  return {
    show(): void {
      minimapWindow.setWindowState('open');
      syncMapTextureIfNeeded();
      applyExpandedLayout();
      redraw();
    },
    hide(): void {
      minimapWindow.setWindowState('closed');
    },
    isVisible(): boolean {
      return minimapWindow.getWindowState() !== 'closed';
    },
    destroy(): void {
      window.clearInterval(intervalId);
      canvas.removeEventListener('click', handleCanvasClick);
      minimapWindow.destroy();
      container.remove();
    }
  };
}
